import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface CohortRow {
  cohort_month:  string;
  month_offset:  number;
  cohort_size:   number;
  retained:      number;
  retention_pct: number;
}

function cellColor(pct: number): string {
  if (pct >= 70) return 'rgba(74,222,128,0.75)';
  if (pct >= 50) return 'rgba(251,191,36,0.65)';
  if (pct >= 30) return 'rgba(251,146,60,0.60)';
  if (pct > 0)   return 'rgba(248,113,113,0.55)';
  return 'rgba(255,255,255,0.04)';
}

function cellTextColor(pct: number): string {
  return pct >= 50 ? '#0D1130' : 'var(--text-primary)';
}

const MAX_OFFSETS = 7;

export function CohortRetentionHeatmap() {
  const [rows,    setRows]    = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    supabase.rpc('get_cohort_retention').then(({ data, error: e }) => {
      if (e) setError(e.message);
      else   setRows((data ?? []) as CohortRow[]);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Calculando coortes…</div>;
  if (error)   return <div style={{ color: '#f87171', fontSize: 'var(--text-caption)' }}>Erro: {error}</div>;
  if (rows.length === 0) return <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic' }}>Sem dados de coorte ainda</div>;

  // Build matrix: cohort_month → { offset: retention_pct }
  const cohorts = [...new Set(rows.map(r => r.cohort_month))].sort();
  const matrix: Record<string, Record<number, CohortRow>> = {};
  for (const r of rows) {
    if (!matrix[r.cohort_month]) matrix[r.cohort_month] = {};
    matrix[r.cohort_month][r.month_offset] = r;
  }

  return (
    <div>
      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Retenção por coorte (pagamentos)
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 400, whiteSpace: 'nowrap' }}>Coorte</th>
              <th style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 400 }}>Usuários</th>
              {Array.from({ length: MAX_OFFSETS }, (_, i) => (
                <th key={i} style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 400, minWidth: '52px' }}>
                  {i === 0 ? 'M0' : `+${i}m`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohorts.map(cohort => {
              const cohortData = matrix[cohort];
              const size = cohortData[0]?.cohort_size ?? 0;
              return (
                <tr key={cohort}>
                  <td style={{ padding: '5px 10px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{cohort}</td>
                  <td style={{ padding: '5px 4px', textAlign: 'center', color: 'var(--text-muted)' }}>{size}</td>
                  {Array.from({ length: MAX_OFFSETS }, (_, i) => {
                    const cell = cohortData[i];
                    const pct  = cell?.retention_pct ?? 0;
                    const isFuture = !cell;
                    return (
                      <td key={i} style={{
                        padding: '5px 4px',
                        textAlign: 'center',
                        backgroundColor: isFuture ? 'transparent' : cellColor(pct),
                        borderRadius: '4px',
                        color: isFuture ? 'var(--text-muted)' : cellTextColor(pct),
                        fontWeight: pct > 0 ? 600 : 400,
                      }}>
                        {isFuture ? '-' : pct > 0 ? `${pct}%` : '0%'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
        M0 = mês do primeiro pagamento · +Nm = pagamento N meses depois
      </p>
    </div>
  );
}
